import { useCallback, useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router-dom'
import { clearWorkerOwner, currentSub, mustChangePassword, pausedSub, refreshSession, resumeQueue } from './lib/auth'
import { drainQueue } from './lib/idb-queue'
import { installDrainOnReconnect } from './lib/drain-on-reconnect'
import { InAppHistoryTracker, seedBoardBeneathDeepLink } from './lib/in-app-history'
import { SessionLossWatcher } from './lib/shell-clear'
import { t, type Lang } from './lib/i18n'
import { PasswordInput } from './components/PasswordInput'
import { LoginScreen } from './features/login/LoginScreen'
import { CockpitScreen } from './features/cockpit/CockpitScreen'
import { OrderDetailScreen } from './features/orders/OrderDetailScreen'
import { NewOrderScreen } from './features/orders/NewOrderScreen'
import { BoardScreen } from './features/board/BoardScreen'
import { StaffScreen } from './features/staff/StaffScreen'
import { AccountScreen } from './features/staff/AccountScreen'
import { MustChangeScreen } from './features/staff/MustChangeScreen'
import { DispatchScreen } from './features/dispatch/DispatchScreen'
import { KitchenScreen } from './features/kitchen/KitchenScreen'
import { KitchenTvScreen } from './features/kitchen/KitchenTvScreen'
import { SettingsScreen } from './features/settings/SettingsScreen'

const BASENAME = import.meta.env.BASE_URL.replace(/\/$/, '') || '/'

// R413 (c): secureFetch fires hop:password-change-required on a 403 PASSWORD_CHANGE_REQUIRED,
// and the re-auth overlay fires it after a flagged sign-in. This sits inside the router so it
// can navigate; it renders nothing.
function PasswordChangeRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    const handler = () => navigate('/change-password', { replace: true })
    window.addEventListener('hop:password-change-required', handler)
    return () => window.removeEventListener('hop:password-change-required', handler)
  }, [navigate])
  return null
}

// ux1 (f): the language choice survives a reload. Only the two-letter code is stored, under
// its own key; it is a preference, not a credential (lib/auth.ts keeps every token out of
// storage). A saved choice wins over the default; no saved choice means the default.
export const LANG_STORAGE_KEY = 'hop.lang'
const DEFAULT_LANG: Lang = 'en'

function readSavedLang(): Lang {
  try {
    const saved = window.localStorage.getItem(LANG_STORAGE_KEY)
    return saved === 'en' || saved === 'ar' ? saved : DEFAULT_LANG
  } catch {
    return DEFAULT_LANG
  }
}

function saveLang(lang: Lang): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang)
  } catch {
    // Storage blocked (private mode): the choice lasts for this session only.
  }
}

export default function App() {
  // R492: before BrowserRouter reads window.history (this initializer runs before the router
  // renders), a deep link gets the Board written beneath it, so the browser's back = Back.
  useState(() => seedBoardBeneathDeepLink(BASENAME))
  const [lang, setLang] = useState<Lang>(readSavedLang)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [reAuthVisible, setReAuthVisible] = useState(false)
  const [reAuthUsername, setReAuthUsername] = useState('')
  const [reAuthPassword, setReAuthPassword] = useState('')
  const [reAuthSubmitting, setReAuthSubmitting] = useState(false)
  const [reAuthError, setReAuthError] = useState<string | null>(null)

  const handleLangToggle = useCallback(() => {
    const next: Lang = lang === 'en' ? 'ar' : 'en'
    setLang(next)
    saveLang(next)
  }, [lang])

  // Track online/offline state
  useEffect(() => {
    const up = () => setIsOnline(true)
    const down = () => setIsOnline(false)
    window.addEventListener('online', up)
    window.addEventListener('offline', down)
    return () => {
      window.removeEventListener('online', up)
      window.removeEventListener('offline', down)
    }
  }, [])

  // bench-QUEUE-DRAINS-ON-RECONNECT.CB-E §1.2 — drain the offline queue on reconnect,
  // no reload required. Separate from the isOnline effect above, which stays unchanged.
  useEffect(() => installDrainOnReconnect(window), [])

  // hop:auth-required (window CustomEvent) — show re-auth overlay
  // CRITICAL: window channel only, NOT the SW postMessage HOP_AUTH_REQUIRED channel
  useEffect(() => {
    const handler = () => {
      setReAuthVisible(true)
      setReAuthError(null)
    }
    window.addEventListener('hop:auth-required', handler)
    return () => window.removeEventListener('hop:auth-required', handler)
  }, [])

  const handleReAuthSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      setReAuthError(null)
      setReAuthSubmitting(true)
      try {
        // R413 (c), Frank #62950: no worker owner while the cookie may be changing hands.
        await clearWorkerOwner()
        const res = await fetch('/auth/login', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: reAuthUsername, password: reAuthPassword }),
        })
        if (res.ok) {
          // Gateway sets the httpOnly ops_session cookie itself; no token ever reaches JS.
          await refreshSession()
          if (mustChangePassword()) {
            // R413 (c): same branch as LoginScreen. The queue stays paused: every write
            // would answer 403 PASSWORD_CHANGE_REQUIRED until the change is made. The
            // router-side listener takes the person to /change-password.
            setReAuthVisible(false)
            setReAuthUsername('')
            setReAuthPassword('')
            window.dispatchEvent(new CustomEvent('hop:password-change-required'))
            return
          }
          // R413 (c), Rook #62940: unowned (pre-sub) writes drain only when the person
          // re-authenticating is the one whose session the 401 paused. Read before
          // resumeQueue(), which forgets the paused owner.
          const paused = pausedSub()
          const sameSession = paused !== null && paused === currentSub()
          resumeQueue()
          drainQueue({ includeUnowned: sameSession }).catch(() => {})
          setReAuthVisible(false)
          setReAuthUsername('')
          setReAuthPassword('')
        } else {
          setReAuthError(t(lang, 'loginFailed'))
        }
      } catch {
        setReAuthError(t(lang, 'loginFailed'))
      } finally {
        setReAuthSubmitting(false)
      }
    },
    [reAuthUsername, reAuthPassword, lang],
  )

  return (
    <>
      <BrowserRouter basename={BASENAME}>
        <PasswordChangeRedirect />
        <InAppHistoryTracker />
        <SessionLossWatcher />
        <Routes>
          <Route path="/" element={<Navigate to="/cockpit" replace />} />
          <Route
            path="/login"
            element={
              <LoginScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/cockpit"
            element={
              <CockpitScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/board"
            element={
              <BoardScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/kitchen"
            element={
              <KitchenScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/kitchen/tv"
            element={
              <KitchenTvScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/orders/new"
            element={
              <NewOrderScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/orders/:id"
            element={
              <OrderDetailScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/staff"
            element={
              <StaffScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/settings"
            element={
              <SettingsScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/dispatch"
            element={
              <DispatchScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/account"
            element={
              <AccountScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
          <Route
            path="/change-password"
            element={
              <MustChangeScreen lang={lang} onLangToggle={handleLangToggle} isOnline={isOnline} />
            }
          />
        </Routes>
      </BrowserRouter>

      {reAuthVisible && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-card rounded-xl border border-border shadow-xl p-8 w-full max-w-sm">
            <p className="text-sm text-muted-foreground mb-5">{t(lang, 'authRequired')}</p>
            <form onSubmit={handleReAuthSubmit} className="space-y-4">
              {reAuthError && (
                <div className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2 text-sm text-destructive">
                  {reAuthError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t(lang, 'username')}
                </label>
                <input
                  type="text"
                  value={reAuthUsername}
                  onChange={(e) => setReAuthUsername(e.target.value)}
                  required
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  {t(lang, 'password')}
                </label>
                <PasswordInput
                  lang={lang}
                  value={reAuthPassword}
                  onChange={(e) => setReAuthPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <button
                type="submit"
                disabled={reAuthSubmitting}
                className="w-full rounded-md bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors min-h-[44px]"
              >
                {reAuthSubmitting ? t(lang, 'signingIn') : t(lang, 'reauth')}
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  )
}
